import React, { useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { parseCVText } from './cvParser';

// ── Sortable wrapper for body sections ───────────────────────────────────────
function SortableSection({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </Box>
  );
}

// ── Main preview component ───────────────────────────────────────────────────
const CVPreview = ({ content, config, sections: sectionsProp, onSectionsChange }) => {
  const {
    fontFamily = 'Tahoma',
    fontSize = 10,
    lineHeight = 1.4,
    primaryColor = '#191970',
    secondaryColor = '#000000',
    headingColor = '#191970',
    boldHeadings = true,
    scale = 1.4,
  } = config || {};

  const sections = useMemo(
    () => sectionsProp || parseCVText(content),
    [sectionsProp, content],
  );

  const draggable = !!onSectionsChange;

  // Separate header (fixed) from body sections (draggable)
  const headerSection = useMemo(() => sections.find((s) => s.type === 'header'), [sections]);
  const bodySections = useMemo(
    () => sections.filter((s) => s.type !== 'header' && !s.hidden),
    [sections],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onSectionsChange) return;

      const allSections = [...sections];
      const bodyOnly = allSections.filter((s) => s.type !== 'header');
      const oldIndex = bodyOnly.findIndex((s) => s.id === active.id);
      const newIndex = bodyOnly.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(bodyOnly, oldIndex, newIndex);
      const headerSections = allSections.filter((s) => s.type === 'header');
      onSectionsChange([...headerSections, ...reordered]);
    },
    [sections, onSectionsChange],
  );

  const getFontFamily = (font) => {
    switch (font) {
      case 'Tahoma': return 'Tahoma, Geneva, Verdana, sans-serif';
      case 'PTSans': return '"PTSans", "Trebuchet MS", sans-serif';
      case 'TimesNewRoman': return '"TimesNewRoman", "Times New Roman", Times, serif';
      case 'Helvetica': return 'Helvetica, Arial, sans-serif';
      case 'Arial': return 'Arial, Helvetica, sans-serif';
      default: return font;
    }
  };

  const baseFontSize = fontSize * scale;
  const styles = useMemo(() => ({
    container: {
      padding: '40px',
      backgroundColor: '#FFFFFF',
      fontFamily: getFontFamily(fontFamily),
      color: secondaryColor,
      lineHeight: lineHeight,
      minHeight: '100%',
      boxSizing: 'border-box',
    },
    headerName: {
      fontSize: 2.2 * baseFontSize, color: secondaryColor,
      fontWeight: 'bold', marginBottom: '16px', textAlign: 'center', fontFamily: 'inherit',
    },
    headerTitle: {
      fontSize: 1.3 * baseFontSize, color: secondaryColor,
      marginBottom: '16px', textAlign: 'center', fontFamily: 'inherit',
    },
    headerContact: {
      fontSize: baseFontSize, color: primaryColor,
      marginBottom: '12px', textAlign: 'center', fontFamily: 'inherit',
    },
    sectionTitle: {
      fontSize: 1.3 * baseFontSize, color: headingColor,
      fontWeight: boldHeadings ? 'bold' : 'normal', textTransform: 'uppercase',
      marginTop: '20px', marginBottom: '10px',
      borderBottom: `2px solid ${headingColor}`, paddingBottom: '4px', fontFamily: 'inherit',
    },
    experienceTitle: {
      fontSize: 1.1 * baseFontSize, color: primaryColor,
      fontWeight: 'bold', marginTop: '8px', marginBottom: '4px', fontFamily: 'inherit',
    },
    text: { fontSize: baseFontSize, marginBottom: '4px', fontFamily: 'inherit' },
    bulletPoint: { display: 'flex', flexDirection: 'row', marginBottom: '4px', paddingLeft: '16px' },
    bullet: { width: '16px', flexShrink: 0, fontSize: baseFontSize, fontFamily: 'inherit' },
    checkLine: { display: 'flex', flexDirection: 'row', marginTop: '4px', marginBottom: '4px', paddingLeft: '8px' },
    checkMark: { color: primaryColor, fontWeight: 'bold', marginRight: '8px', fontSize: baseFontSize, fontFamily: 'inherit' },
  }), [fontFamily, fontSize, lineHeight, primaryColor, secondaryColor, headingColor, boldHeadings, baseFontSize, scale]);

  const renderLine = (line, idx) => {
    switch (line.type) {
      case 'empty':
        return <Box key={idx} sx={{ height: 10 }} />;
      case 'headerName':
        return <Typography key={idx} sx={styles.headerName}>{line.text}</Typography>;
      case 'headerTitle':
        return <Typography key={idx} sx={styles.headerTitle}>{line.text}</Typography>;
      case 'headerContact':
        return <Typography key={idx} sx={styles.headerContact}>{line.text}</Typography>;
      case 'role':
        return (
          <Typography key={idx} sx={{ ...styles.experienceTitle, marginBottom: '2px', marginTop: 0 }}>
            {line.text}
          </Typography>
        );
      case 'experienceHeader':
        return (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', marginTop: '8px', marginBottom: '2px' }}>
            <Typography sx={{ ...styles.experienceTitle, marginTop: 0, flexShrink: 0 }}>
              {line.titlePart}
            </Typography>
            {line.infoPart && (
              <Typography sx={{ ...styles.text, marginLeft: '6px', flex: 1, marginTop: '2px' }}>
                | {line.infoPart}
              </Typography>
            )}
          </Box>
        );
      case 'boldCategory':
        return (
          <Typography key={idx} sx={{ ...styles.text, marginTop: '4px' }}>
            <span style={{ fontWeight: 'bold' }}>{line.boldPart}</span>
            {' ' + line.regularPart}
          </Typography>
        );
      case 'boldLine':
        return (
          <Typography key={idx} sx={{ ...styles.experienceTitle, color: secondaryColor }}>
            {line.text}
          </Typography>
        );
      case 'check':
        return (
          <Box key={idx} sx={styles.checkLine}>
            <Typography sx={styles.checkMark}>•</Typography>
            <Typography sx={styles.text}>{line.text}</Typography>
          </Box>
        );
      case 'bullet':
        return (
          <Box key={idx} sx={styles.bulletPoint}>
            <Typography sx={styles.bullet}>•</Typography>
            <Typography sx={styles.text}>{line.text}</Typography>
          </Box>
        );
      default:
        return <Typography key={idx} sx={styles.text}>{line.text}</Typography>;
    }
  };

  // ── Render a single body section with optional drag handle on the title ─
  const renderBodySection = (section, dragHandleProps, isDragging) => {
    const align = section.align || 'left';
    return (
      <Box sx={{
        textAlign: align,
        border: isDragging ? `2px dashed ${headingColor}` : '2px solid transparent',
        borderRadius: '4px',
        transition: 'border-color 0.2s',
      }}>
        {section.title ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {draggable && (
              <Box
                {...dragHandleProps}
                sx={{
                  cursor: 'grab',
                  opacity: 0.3,
                  transition: 'opacity 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  color: headingColor,
                  flexShrink: 0,
                  mr: 0.5,
                  '&:hover': { opacity: 1 },
                  '&:active': { cursor: 'grabbing', opacity: 1 },
                }}
              >
                <DragIcon fontSize="small" />
              </Box>
            )}
            <Typography sx={{ ...styles.sectionTitle, flex: 1, marginTop: 0 }}>
              {section.title}
            </Typography>
          </Box>
        ) : draggable ? (
          <Box
            {...dragHandleProps}
            sx={{
              cursor: 'grab',
              opacity: 0.3,
              display: 'inline-flex',
              alignItems: 'center',
              color: headingColor,
              mb: 0.5,
              '&:hover': { opacity: 1 },
            }}
          >
            <DragIcon fontSize="small" />
          </Box>
        ) : null}
        {section.lines.map(renderLine)}
      </Box>
    );
  };

  if (sections.length === 0) return null;

  const bodyContent = draggable ? (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={bodySections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        {bodySections.map((section) => (
          <SortableSection key={section.id} id={section.id}>
            {({ dragHandleProps, isDragging }) => renderBodySection(section, dragHandleProps, isDragging)}
          </SortableSection>
        ))}
      </SortableContext>
    </DndContext>
  ) : (
    bodySections.map((section) => (
      <Box key={section.id}>{renderBodySection(section)}</Box>
    ))
  );

  return (
    <Box sx={styles.container}>
      {headerSection && !headerSection.hidden && (
        <Box sx={{ marginBottom: '32px', paddingBottom: '16px', textAlign: headerSection.align || 'center' }}>
          {headerSection.lines.map(renderLine)}
        </Box>
      )}
      {bodyContent}
    </Box>
  );
};

export default CVPreview;
